import { ChangeDetectionStrategy, Component, effect, inject, input } from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { EChartsOption, BarSeriesOption, LineSeriesOption } from 'echarts';
import { echarts } from '../echarts';
import { ChartsService } from '../../services/charts/charts.service';
import { ChartColors } from '../../common/general-types/chart.types';
import { ShortNumberPipe } from '../../common/pipes/short-number/short-number.pipe';

@Component({
  selector: 'app-echart-generic-chart',
  templateUrl: './echart-generic-chart.component.html',
  styleUrl: './echart-generic-chart.component.scss',
  imports: [NgxEchartsDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EchartGenericChartComponent {
  chartOption = input.required<EChartsOption>();
  chartXaxisData = input.required<string[]>();
  chartVariant = input<
    | 'default'
    | 'timeline'
    | 'timelineBar'
    | 'timelineBarPurple'
    | 'timelineBarPink'
    | 'timelineBarDark'
    | 'timelineBarPinkDark'
  >('default');
  chartTitle = input<string | undefined>(undefined);

  isDataAvailable = false;
  styledChartOption!: EChartsOption;
  chartTheme!: ChartColors;

  private readonly chartsService = inject(ChartsService);
  private readonly shortNumberPipe = new ShortNumberPipe();

  constructor() {
    this.chartTheme = this.chartsService.chartColorsLightTheme;

    effect(() => {
      this.chartTheme = this.chartsService.chartTheme();
      const option = this.chartOption();
      this.chartXaxisData();
      this.chartVariant();

      if (option) {
        this.isDataAvailable = true;
        this.styledChartOption = { ...this.applyStyles(option) };
      }
    });
  }

  private applyStyles(option: EChartsOption): EChartsOption {
    const variant = this.chartVariant();
    const isTimeline = variant === 'timeline';
    const isTimelineBar =
      variant === 'timelineBar' ||
      variant === 'timelineBarPurple' ||
      variant === 'timelineBarPink' ||
      variant === 'timelineBarDark' ||
      variant === 'timelineBarPinkDark';
    const isTimelineBarDark = variant === 'timelineBarDark' || variant === 'timelineBarPinkDark';
    const isTimelineStyle = isTimeline || isTimelineBar;

    const styled: EChartsOption = {
      ...option,
      title: this.chartTitle()
        ? {
            text: this.chartTitle(),
            left: 0,
            top: 0,
            textStyle: {
              color: '#374151',
              fontSize: 16,
              fontWeight: 600,
            },
          }
        : option.title,
      grid: {
        ...option.grid,
        left: isTimelineStyle ? '5%' : '3%',
        right: isTimelineStyle ? '5%' : '3%',
        bottom: isTimelineStyle ? '14%' : '10%',
        top: this.chartTitle() ? 52 : isTimelineStyle ? '8%' : '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: this.chartXaxisData(),
        axisLine: { show: true, lineStyle: { color: isTimelineBarDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb' } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          show: true,
          color: isTimelineBarDark ? '#a1a1aa' : this.chartTheme.xAxis.xAxisLabel,
          fontSize: 12,
        },
        boundaryGap: true,
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: isTimelineBarDark,
          lineStyle: { color: 'rgba(255,255,255,0.06)' },
        },
        axisLabel: {
          show: true,
          color: isTimelineBarDark ? '#a1a1aa' : this.chartTheme.yAxis.yAxisLabel,
          formatter: (value: number) => this.formatShortNumber(value),
        },
      },
      tooltip: {
        ...option.tooltip,
        trigger: 'axis',
        backgroundColor: this.chartTheme.tooltip.background,
        borderColor: this.chartTheme.tooltip.border,
        textStyle: { color: this.chartTheme.tooltip.text },
        formatter: (params: unknown) => {
          const items = (Array.isArray(params) ? params : [params]) as Array<{
            axisValueLabel?: string;
            seriesName?: string;
            marker?: string;
            value?: number | string;
          }>;

          if (items.length === 0) {
            return '';
          }

          const title = items[0]?.axisValueLabel ?? '';
          const rows = items
            .map((item) => {
              const value = Number(item.value ?? 0).toLocaleString();
              return `${item.marker ?? ''}${item.seriesName ?? ''}: <strong>${value}</strong>`;
            })
            .join('<br/>');

          return title ? `${title}<br/>${rows}` : rows;
        },
      },
    };

    const gradientLine = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
      { offset: 0, color: '#4E46DC' },
      { offset: 0.4, color: '#6B5BD4' },
      { offset: 1, color: '#D83079' },
    ]);

    const gradientArea = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: 'rgba(78, 70, 220, 0.25)' },
      { offset: 1, color: 'rgba(216, 48, 121, 0)' },
    ]);

    const gradientBar = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: '#D83079' },
      { offset: 0.45, color: '#6B5BD4' },
      { offset: 1, color: '#4E46DC' },
    ]);

    const gradientBarPurple = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: '#8b5cf6' },
      { offset: 0.55, color: '#6366f1' },
      { offset: 1, color: '#38bdf8' },
    ]);

    const gradientBarPink = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: '#f472b6' },
      { offset: 0.55, color: '#d946ef' },
      { offset: 1, color: '#8b5cf6' },
    ]);

    if (Array.isArray(option.series)) {
      styled.series = option.series.map((series) => {
        if (isTimelineBar) {
          const barSeries = series as Partial<BarSeriesOption>;
          const barColor =
            variant === 'timelineBarPinkDark' || variant === 'timelineBarPink'
              ? gradientBarPink
              : variant === 'timelineBarDark' || variant === 'timelineBarPurple'
                ? gradientBarPurple
                : gradientBar;
          return {
            ...barSeries,
            type: 'bar',
            barMaxWidth: 28,
            itemStyle: {
              color: barSeries.itemStyle?.color ?? barColor,
              borderRadius: [6, 6, 0, 0],
            },
            label: { show: false },
            emphasis: { focus: 'series' },
          } as BarSeriesOption;
        }

        const lineSeries = series as Partial<LineSeriesOption>;
        if (isTimeline) {
          return {
            ...lineSeries,
            type: 'line',
            smooth: true,
            symbol: 'none',
            symbolSize: 0,
            lineStyle: {
              width: 2.5,
              color: gradientLine,
            },
            areaStyle: {
              color: gradientArea,
            },
            label: { show: false },
            emphasis: { scale: false, focus: 'series' },
          } as LineSeriesOption;
        }
        return {
          ...lineSeries,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: this.chartTheme.series.marker.background,
            borderWidth: 1,
            borderColor: this.chartTheme.series.marker.border,
          },
          lineStyle: {
            width: 3,
            color: this.chartTheme.series.line,
          },
          label: {
            show: true,
            position: 'top',
            formatter: (params) => {
              const value = params.value;
              if (typeof value === 'number') {
                return this.formatShortNumber(value);
              }
              return '';
            },
            color: this.chartTheme.series.label,
            fontSize: 14,
            fontWeight: 'bold',
          },
          emphasis: { scale: true },
        } as LineSeriesOption;
      });
    }

    return styled;
  }

  private formatShortNumber(value: number): string {
    return this.shortNumberPipe.transform(value);
  }
}
